import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  qty: number;
  image?: string;
}

const CART_STORAGE_KEY = "sewar_cart_v1";
const CART_URL_PARAM = "cart";

/** Compact cart encoding for share links: [{i:id, n:name, p:price, q:qty}] as base64url JSON */
export function encodeCart(items: CartItem[]): string {
  const compact = items.map((it) => ({ i: it.id, n: it.name, p: it.price, q: it.qty }));
  const json = JSON.stringify(compact);
  return btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function decodeCart(encoded: string): CartItem[] {
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(escape(atob(b64)));
    const compact = JSON.parse(json) as Array<{ i: string; n: string; p: number; q: number }>;
    if (!Array.isArray(compact)) return [];
    return compact
      .filter((it) => it && typeof it.i === "string" && typeof it.n === "string")
      .map((it) => ({
        id: it.i,
        name: it.n,
        price: Number(it.p) || 0,
        qty: Math.max(1, Math.min(99, Number(it.q) || 1)),
      }));
  } catch {
    return [];
  }
}

export function buildCartShareUrl(items: CartItem[]): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  url.pathname = "/menu";
  url.searchParams.set(CART_URL_PARAM, encodeCart(items));
  return url.toString();
}

interface CartContextValue {
  items: CartItem[];
  totalQty: number;
  totalPrice: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "qty">, qty?: number) => void;
  setQty: (id: string, qty: number) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate: shared link param wins, otherwise localStorage
  useEffect(() => {
    if (hydrated || typeof window === "undefined") return;
    setHydrated(true);

    const param = new URLSearchParams(window.location.search).get(CART_URL_PARAM);
    if (param) {
      const shared = decodeCart(param);
      if (shared.length > 0) {
        setItems(shared);
        setIsOpen(true);
        return;
      }
    }

    try {
      const stored = window.localStorage.getItem(CART_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CartItem[];
        if (Array.isArray(parsed)) setItems(parsed.filter((it) => it && it.id));
      }
    } catch {}
  }, [hydrated]);

  // Persist
  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items, hydrated]);

  const addItem = (item: Omit<CartItem, "qty">, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.id === item.id);
      if (existing) {
        return prev.map((it) =>
          it.id === item.id ? { ...it, qty: Math.min(99, it.qty + qty) } : it,
        );
      }
      return [...prev, { ...item, qty }];
    });
  };

  const setQty = (id: string, qty: number) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((it) => it.id !== id)
        : prev.map((it) => (it.id === id ? { ...it, qty: Math.min(99, qty) } : it)),
    );
  };

  const removeItem = (id: string) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const clearCart = () => setItems([]);

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      totalQty: items.reduce((s, it) => s + it.qty, 0),
      totalPrice: items.reduce((s, it) => s + it.qty * it.price, 0),
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      addItem,
      setQty,
      removeItem,
      clearCart,
    }),
    [items, isOpen],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
